import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Building2, Activity, CalendarClock, AlertTriangle, ArrowUpRight, Landmark, Target, ClipboardList, Sparkles, Settings, GripVertical, ArrowUp, ArrowDown, Users2 } from "lucide-react";
import { Link } from "react-router-dom";
import RepTiles from "@/components/RepTiles";
import RepLeaderboard from "@/components/RepLeaderboard";
import StreakPodium from "@/components/StreakPodium";
import MarketingROI from "@/components/MarketingROI";
import MarketingBudget from "@/components/MarketingBudget";
import DashboardCalendar from "@/components/DashboardCalendar";
import RepWorksheet from "@/components/RepWorksheet";

// Tile palette — solid colored surfaces (never dark-text-on-dark). Avoids teal per design guidelines.
const TILE_DEFS = {
  facilities_total: { label: "Total facilities", icon: Building2, tone: "indigo", read: (s) => ({ value: s.facilities.total, sub: `${s.facilities.snf} SNF · ${s.facilities.alf} ALF` }) },
  active_referrals: { label: "Active referrals", icon: ArrowUpRight, tone: "emerald", read: (s) => ({ value: s.facilities.active, sub: `${s.facilities.prospect} prospects` }) },
  prospects: { label: "Prospects", icon: Target, tone: "amber", read: (s) => ({ value: s.facilities.prospect, sub: "in pipeline" }) },
  visits_7d: { label: "Visits (7d)", icon: Activity, tone: "sky", read: (s) => ({ value: s.visits_last_7, sub: "last 7 days" }) },
  followups_open: { label: "Follow-ups open", icon: CalendarClock, tone: "violet", read: (s) => ({ value: s.followups.open, sub: `${s.followups.overdue} overdue` }) },
  followups_overdue: { label: "Overdue", icon: AlertTriangle, tone: "rose", read: (s) => ({ value: s.followups.overdue, sub: "needs attention" }) },
  snf_count: { label: "SNF facilities", icon: Building2, tone: "slate", read: (s) => ({ value: s.facilities.snf, sub: "skilled nursing" }) },
  alf_count: { label: "ALF facilities", icon: Landmark, tone: "orange", read: (s) => ({ value: s.facilities.alf, sub: "assisted living" }) },
};

const TONE_CLASS = {
  indigo:  { bg: "bg-indigo-600",  soft: "bg-indigo-50 text-indigo-700 border-indigo-100" },
  emerald: { bg: "bg-emerald-600", soft: "bg-emerald-50 text-emerald-700 border-emerald-100" },
  amber:   { bg: "bg-amber-500",   soft: "bg-amber-50 text-amber-800 border-amber-100" },
  sky:     { bg: "bg-sky-600",     soft: "bg-sky-50 text-sky-700 border-sky-100" },
  violet:  { bg: "bg-violet-600",  soft: "bg-violet-50 text-violet-700 border-violet-100" },
  rose:    { bg: "bg-rose-600",    soft: "bg-rose-50 text-rose-700 border-rose-100" },
  slate:   { bg: "bg-slate-900",   soft: "bg-slate-100 text-slate-700 border-slate-200" },
  orange:  { bg: "bg-orange-500",  soft: "bg-orange-50 text-orange-700 border-orange-100" },
};

const DEFAULT_LAYOUT = [
  { key: "facilities_total", visible: true, style: "color" },
  { key: "active_referrals", visible: true, style: "color" },
  { key: "visits_7d", visible: true, style: "color" },
  { key: "followups_open", visible: true, style: "color" },
  { key: "prospects", visible: false, style: "color" },
  { key: "followups_overdue", visible: false, style: "color" },
  { key: "snf_count", visible: false, style: "color" },
  { key: "alf_count", visible: false, style: "color" },
];

const LS_KEY = "aceh_dashboard_v2";

function loadLayout() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return DEFAULT_LAYOUT;
    const saved = JSON.parse(raw);
    // Merge with defaults so newly added tile keys still show up
    const known = new Set(saved.map((t) => t.key));
    const missing = DEFAULT_LAYOUT.filter((t) => !known.has(t.key));
    return [...saved.filter((t) => TILE_DEFS[t.key]), ...missing];
  } catch { return DEFAULT_LAYOUT; }
}
function saveLayout(layout) { localStorage.setItem(LS_KEY, JSON.stringify(layout)); }

function ColorTile({ def, data, style }) {
  const Icon = def.icon;
  const tone = TONE_CLASS[def.tone] || TONE_CLASS.slate;
  if (style === "minimal") {
    return (
      <Card className="p-6 border-slate-200 shadow-none row-enter transition-transform duration-200 hover:-translate-y-[2px]">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest font-mono-plex text-slate-500">{def.label}</div>
            <div className="font-heading text-4xl font-bold mt-2 text-slate-900">{data.value}</div>
            {data.sub && <div className="text-xs text-slate-500 mt-1 font-mono-plex">{data.sub}</div>}
          </div>
          <div className={`w-9 h-9 rounded-md border flex items-center justify-center ${tone.soft}`}>
            <Icon className="w-4 h-4" />
          </div>
        </div>
      </Card>
    );
  }
  return (
    <Card className={`p-6 border-transparent shadow-none row-enter text-white ${tone.bg} transition-transform duration-200 hover:-translate-y-[2px]`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-mono-plex text-white/70">{def.label}</div>
          <div className="font-heading text-4xl font-bold mt-2">{data.value}</div>
          {data.sub && <div className="text-xs text-white/80 mt-1 font-mono-plex">{data.sub}</div>}
        </div>
        <div className="w-9 h-9 rounded-md bg-white/15 backdrop-blur-md flex items-center justify-center">
          <Icon className="w-4 h-4" />
        </div>
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const [s, setS] = useState(null);
  const [reps, setReps] = useState([]);
  const [layout, setLayout] = useState(loadLayout());
  const [customOpen, setCustomOpen] = useState(false);

  useEffect(() => {
    api.get("/dashboard/summary").then(({ data }) => setS(data));
    api.get("/dashboard/reps").then(({ data }) => setReps(data)).catch(() => setReps([]));
  }, []);

  const visibleTiles = useMemo(() => layout.filter((t) => t.visible && TILE_DEFS[t.key]), [layout]);

  const toggleTile = (key) => {
    setLayout((l) => l.map((t) => (t.key === key ? { ...t, visible: !t.visible } : t)));
  };
  const moveTile = (key, dir) => {
    setLayout((l) => {
      const i = l.findIndex((t) => t.key === key);
      if (i < 0) return l;
      const j = dir === "up" ? i - 1 : i + 1;
      if (j < 0 || j >= l.length) return l;
      const copy = [...l];
      [copy[i], copy[j]] = [copy[j], copy[i]];
      return copy;
    });
  };
  const setStyleFor = (key, style) => setLayout((l) => l.map((t) => (t.key === key ? { ...t, style } : t)));
  const resetLayout = () => setLayout(DEFAULT_LAYOUT);
  const saveAndClose = () => { saveLayout(layout); setCustomOpen(false); };

  if (!s) return <div className="p-8 text-slate-500 font-mono-plex text-sm">Loading dashboard…</div>;

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto" data-testid="dashboard-page">
      <div className="mb-8 flex items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest font-mono-plex text-slate-500">Overview</div>
          <h1 className="font-heading text-4xl md:text-5xl font-black text-slate-900 mt-1">Command center</h1>
          <p className="text-slate-500 mt-2 max-w-2xl">Live pulse across Florida SNFs and ALFs your team engages with.</p>
        </div>
        <Dialog open={customOpen} onOpenChange={setCustomOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" data-testid="dashboard-customize" className="border-slate-300"><Settings className="w-4 h-4 mr-1" /> Customize</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Customize dashboard tiles</DialogTitle></DialogHeader>
            <p className="text-xs text-slate-500 font-mono-plex uppercase tracking-widest">Show, hide, reorder, or switch style</p>
            <div className="space-y-2 mt-2">
              {layout.map((t, i) => {
                const def = TILE_DEFS[t.key];
                if (!def) return null;
                const tone = TONE_CLASS[def.tone];
                return (
                  <div key={t.key} className="flex items-center gap-3 border border-slate-200 rounded-md p-3">
                    <GripVertical className="w-4 h-4 text-slate-300" />
                    <div className={`w-2 h-8 rounded-sm ${tone.bg}`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-900">{def.label}</div>
                      <div className="text-[10px] text-slate-500 font-mono-plex uppercase tracking-widest">{def.tone}</div>
                    </div>
                    <div className="flex items-center gap-1 text-xs">
                      <button onClick={() => setStyleFor(t.key, t.style === "color" ? "minimal" : "color")} className="px-2 py-1 border rounded text-[11px] hover:bg-slate-50" data-testid={`tile-style-${t.key}`}>
                        {t.style === "color" ? "Color" : "Minimal"}
                      </button>
                      <button onClick={() => moveTile(t.key, "up")} disabled={i === 0} className="p-1 disabled:opacity-30 hover:bg-slate-100 rounded"><ArrowUp className="w-3.5 h-3.5" /></button>
                      <button onClick={() => moveTile(t.key, "down")} disabled={i === layout.length - 1} className="p-1 disabled:opacity-30 hover:bg-slate-100 rounded"><ArrowDown className="w-3.5 h-3.5" /></button>
                      <Checkbox checked={t.visible} onCheckedChange={() => toggleTile(t.key)} data-testid={`tile-toggle-${t.key}`} />
                    </div>
                  </div>
                );
              })}
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={resetLayout}>Reset default</Button>
              <Button onClick={saveAndClose} data-testid="dashboard-save-layout" className="bg-slate-900 hover:bg-slate-800">Save layout</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {visibleTiles.length === 0 ? (
        <Card className="p-8 text-center text-slate-400 border-dashed border-slate-300 shadow-none">
          No tiles selected. Click <span className="font-medium">Customize</span> to add some.
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {visibleTiles.map((t) => {
            const def = TILE_DEFS[t.key];
            const data = def.read(s);
            return <ColorTile key={t.key} def={def} data={data} style={t.style} />;
          })}
        </div>
      )}

      <RepWorksheet />

      <div className="mt-10 mb-4 flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest font-mono-plex text-slate-500">Team workload</div>
          <h2 className="font-heading text-2xl md:text-3xl font-black text-slate-900 mt-1 flex items-center gap-2"><Users2 className="w-6 h-6" /> Reps &amp; their facilities</h2>
          <p className="text-slate-500 mt-1 text-sm">Facilities per team member, split by Active · Progress · Inactive.</p>
        </div>
      </div>
      <RepTiles reps={reps} />

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mt-8">
        <div className="xl:col-span-2"><RepLeaderboard /></div>
        <StreakPodium />
      </div>

      <div className="mt-8">
        <MarketingBudget />
      </div>

      <div className="mt-8">
        <MarketingROI />
      </div>

      <div className="mt-8">
        <DashboardCalendar />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-8">
        <Card className="lg:col-span-2 p-6 border-slate-200 shadow-none">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-heading font-bold text-xl">Upcoming follow-ups</h3>
              <p className="text-xs text-slate-500 font-mono-plex uppercase tracking-widest">next 7 days</p>
            </div>
            <Link to="/followups" className="text-xs font-mono-plex text-slate-500 hover:text-slate-900">View all →</Link>
          </div>
          <div className="divide-y divide-slate-100">
            {s.upcoming_followups.length === 0 && <div className="text-sm text-slate-400 py-6">Nothing scheduled. Good job.</div>}
            {s.upcoming_followups.map((f) => (
              <div key={f.followup_id} className="py-3 flex items-start justify-between gap-3 row-enter">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">{f.title}</div>
                  <div className="text-xs text-slate-500 mt-0.5"><Link to={`/facilities/${f.facility_id}`} className="hover:text-indigo-700 hover:underline">{f.facility_name}</Link></div>
                </div>
                <div className="text-right">
                  <div className="text-xs font-mono-plex text-slate-900">{f.due_date}</div>
                  <Badge variant="outline" className={`mt-1 text-[10px] ${f.priority === "high" ? "border-rose-300 text-rose-700" : f.priority === "medium" ? "border-orange-300 text-orange-700" : "border-slate-300 text-slate-600"}`}>{f.priority}</Badge>
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-6 border-slate-200 shadow-none">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-bold text-xl">By county</h3>
            <span className="text-xs text-slate-400 font-mono-plex uppercase">density</span>
          </div>
          <div className="space-y-3">
            {s.by_county.length === 0 && <div className="text-sm text-slate-400">No facilities yet.</div>}
            {s.by_county.map((c, idx) => {
              const max = Math.max(...s.by_county.map((x) => x.count), 1);
              const pct = (c.count / max) * 100;
              const palette = ["bg-indigo-600", "bg-emerald-600", "bg-amber-500", "bg-sky-600", "bg-violet-600", "bg-rose-600", "bg-orange-500", "bg-slate-900"];
              return (
                <div key={c.county}>
                  <div className="flex justify-between text-xs font-mono-plex text-slate-600 mb-1">
                    <span>{c.county}</span>
                    <span>{c.count}</span>
                  </div>
                  <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${palette[idx % palette.length]}`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      </div>

      <Card className="p-6 border-slate-200 shadow-none mt-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-bold text-xl">Recent visits</h3>
          <Link to="/visits" className="text-xs font-mono-plex text-slate-500 hover:text-slate-900">View all →</Link>
        </div>
        <div className="divide-y divide-slate-100">
          {s.recent_visits.length === 0 && <div className="text-sm text-slate-400 py-6">No visits recorded yet.</div>}
          {s.recent_visits.map((v) => (
            <div key={v.visit_id} className="py-3 flex items-start justify-between gap-3 row-enter">
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-900 truncate"><Link to={`/facilities/${v.facility_id}`} className="hover:text-indigo-700 hover:underline" data-testid={`dash-visit-link-${v.visit_id}`}>{v.facility_name}</Link></div>
                <div className="text-xs text-slate-500 mt-0.5 line-clamp-1">{v.purpose}</div>
              </div>
              <div className="text-right">
                <div className="text-xs font-mono-plex text-slate-900">{v.visit_date}</div>
                <div className="text-[11px] text-slate-500">{v.created_by_name}</div>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {s.followups.overdue > 0 && (
        <div className="mt-6 flex items-center gap-2 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md p-4">
          <AlertTriangle className="w-4 h-4" />
          {s.followups.overdue} overdue follow-up{s.followups.overdue > 1 ? "s" : ""} need attention.
        </div>
      )}
    </div>
  );
}
